import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ProtectedMatrikelAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/* ── helpers ─────────────────────────────────────────────────────────── */

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  
  return (
    <div style={{
      position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 1000,
      background: type === 'error' ? 'var(--danger)' : 'var(--forest)',
      color: '#fff', padding: '0.85rem 1.25rem', borderRadius: 6,
      boxShadow: '0 4px 20px rgba(0,0,0,.25)',
      display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.88rem'
    }}>
      <span>{type === 'error' ? '✕' : '✓'}</span>
      <span>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.7)', cursor: 'pointer', fontSize: '1rem', padding: 0 }}>×</button>
    </div>
  );
}

// Inline editable cell component
function EditableCell({ value, onSave, isEditing, setIsEditing, disabled, isTextArea, onTabNext, onTabPrev }) {
  const [editValue, setEditValue] = useState(value || '');
  const inputRef = useRef(null);
  
  useEffect(() => {
    setEditValue(value || '');
  }, [value]);
  
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSave(editValue);
      setIsEditing(false);
    } else if (e.key === 'Escape') {
      setEditValue(value || '');
      setIsEditing(false);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      onSave(editValue);
      setIsEditing(false);
      if (e.shiftKey) {
        onTabPrev?.();
      } else {
        onTabNext?.();
      }
    }
  };
  
  const handleBlur = () => {
    onSave(editValue);
    setIsEditing(false);
  };
  
  const displayValue = value ? String(value).replace(/\n/g, ' / ') : '-';
  
  if (isEditing && !disabled) {
    const inputStyle = {
      width: '100%',
      minHeight: isTextArea ? '60px' : 'auto',
      padding: '0.4rem 0.6rem',
      border: '2px solid var(--forest)',
      borderRadius: 4,
      fontSize: '0.85rem',
      fontFamily: 'Lato, sans-serif',
      resize: 'vertical',
      outline: 'none'
    };
    
    if (isTextArea) {
      return (
        <textarea
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          style={inputStyle}
          rows={3}
        />
      );
    }
    
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        style={{ ...inputStyle, minHeight: 'auto' }}
      />
    );
  }
  
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.35rem' }}>
      <span 
        onClick={() => !disabled && setIsEditing(true)}
        style={{ 
          cursor: disabled ? 'default' : 'pointer',
          display: 'block',
          minHeight: '1.5rem',
          flex: 1
        }}
      >
        {displayValue}
      </span>
      {!disabled && (
        <button
          onClick={() => setIsEditing(true)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.1rem',
            fontSize: '0.75rem',
            opacity: 0.5,
            color: 'var(--forest)',
            flexShrink: 0
          }}
          title="Redigera"
        >
          ✏️
        </button>
      )}
    </div>
  );
}

// Delete confirmation component
function DeleteConfirmModal({ onConfirm, onCancel, entry }) {
  return (
    <>
      <div 
        onClick={onCancel}
        style={{ 
          position: 'fixed', 
          inset: 0, 
          background: 'rgba(0,0,0,.5)', 
          zIndex: 200 
        }} 
      />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'var(--white)',
        padding: '2rem',
        borderRadius: 8,
        zIndex: 201,
        maxWidth: '400px',
        width: '90%',
        boxShadow: '0 10px 40px rgba(0,0,0,.3)'
      }}>
        <h3 style={{ margin: '0 0 1rem', color: 'var(--danger)' }}>⚠️ Bekräfta borttagning</h3>
        <p style={{ margin: '0 0 1.5rem' }}>
          Är du säker på att du vill ta bort tomten <strong>{entry.plot}</strong> ({entry.property})?
          <br /><br />
          Denna åtgärd kan inte ångras.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button 
            onClick={onCancel}
            className="btn btn-ghost"
            style={{ flex: 1 }}
          >
            Avbryt
          </button>
          <button 
            onClick={onConfirm}
            className="btn btn-danger"
            style={{ 
              flex: 1, 
              background: 'var(--danger)', 
              color: '#fff',
              border: 'none',
              padding: '0.6rem 1.2rem',
              borderRadius: 4,
              cursor: 'pointer'
            }}
          >
            Ta bort
          </button>
        </div>
      </div>
    </>
  );
}

// Add entry form modal
function AddEntryModal({ initialPlot, onSave, onCancel, saving }) {
  const [form, setForm] = useState({ plot: initialPlot, property: '', owner: '', phone: '', email: '', road: '' });
  const [error, setError] = useState('');

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.owner.trim() || !form.property.trim()) {
      setError('Ägare och fastighet måste fyllas i');
      return;
    }
    setError('');
    onSave(form);
  };

  const fieldStyle = {
    width: '100%',
    padding: '0.5rem 0.7rem',
    border: '1px solid var(--border)',
    borderRadius: 4,
    fontSize: '0.88rem',
    fontFamily: 'Lato, sans-serif'
  };
  const labelStyle = { display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem', color: 'var(--text-soft)' };

  return (
    <>
      <div
        onClick={saving ? undefined : onCancel}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,.5)',
          zIndex: 200
        }}
      />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'var(--white)',
        padding: '2rem',
        borderRadius: 8,
        zIndex: 201,
        maxWidth: '480px',
        width: '90%',
        maxHeight: '85vh',
        overflowY: 'auto',
        boxShadow: '0 10px 40px rgba(0,0,0,.3)'
      }}>
        <h3 style={{ margin: '0 0 1.25rem', color: 'var(--forest)' }}>Lägg till rad</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: '0.9rem', marginBottom: error ? '0.75rem' : '1.5rem' }}>
            <div>
              <label style={labelStyle}>Tomtnr</label>
              <input style={fieldStyle} value={form.plot} onChange={set('plot')} disabled={saving} />
            </div>
            <div>
              <label style={labelStyle}>Fastighet *</label>
              <input style={fieldStyle} value={form.property} onChange={set('property')} disabled={saving} required />
            </div>
            <div>
              <label style={labelStyle}>Ägare *</label>
              <input style={fieldStyle} value={form.owner} onChange={set('owner')} disabled={saving} required />
            </div>
            <div>
              <label style={labelStyle}>Telefon</label>
              <input style={fieldStyle} value={form.phone} onChange={set('phone')} disabled={saving} />
            </div>
            <div>
              <label style={labelStyle}>E-post</label>
              <input style={fieldStyle} type="email" value={form.email} onChange={set('email')} disabled={saving} />
            </div>
            <div>
              <label style={labelStyle}>Väg</label>
              <input style={fieldStyle} value={form.road} onChange={set('road')} disabled={saving} />
            </div>
          </div>
          {error && <p style={{ color: 'var(--danger)', fontSize: '0.8rem', margin: '0 0 1rem' }}>{error}</p>}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button type="button" onClick={onCancel} className="btn btn-ghost" style={{ flex: 1 }} disabled={saving}>
              Avbryt
            </button>
            <button type="submit" className="btn btn-forest" style={{ flex: 1 }} disabled={saving}>
              {saving ? 'Sparar...' : 'Lägg till'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

/* ── main component ──────────────────────────────────────────────────── */

export default function MatrikelManager() {
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const canEdit = isAdmin || user?.role === 'sekreterare';
  
  const [matrikel, setMatrikel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [exportLoading, setExportLoading] = useState({ csv: false, emails: false });
  
  // Filters
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    owner: '',
    property: '',
    phone: '',
    plot: '',
    email: '',
    road: ''
  });
  
  const notify = (message, type = 'success') => setToast({ message, type });
  
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ProtectedMatrikelAPI.getAll();
      setMatrikel(data.matrikel || []);
    } catch (err) {
      notify('Kunde inte ladda matrikel', 'error');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => { load(); }, [load]);
  
  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    const options = {
      owner: [...new Set(matrikel.map(m => m.owner).filter(Boolean))].sort(),
      property: [...new Set(matrikel.map(m => m.property).filter(Boolean))].sort(),
      phone: [...new Set(matrikel.map(m => m.phone).filter(Boolean))].sort(),
      plot: [...new Set(matrikel.map(m => m.plot).filter(Boolean))].sort((a, b) => parseInt(a) - parseInt(b)),
      email: [...new Set(matrikel.map(m => m.email).filter(Boolean))].sort(),
      road: [...new Set(matrikel.map(m => m.road).filter(Boolean))].sort()
    };
    return options;
  }, [matrikel]);
  
  // Filter and search
  const filtered = useMemo(() => {
    return matrikel.filter(entry => {
      // Global search
      if (search) {
        const searchLower = search.toLowerCase();
        const match = 
          (entry.owner || '').toLowerCase().includes(searchLower) ||
          (entry.property || '').toLowerCase().includes(searchLower) ||
          (entry.phone || '').toLowerCase().includes(searchLower) ||
          (entry.plot || '').toLowerCase().includes(searchLower) ||
          (entry.email || '').toLowerCase().includes(searchLower) ||
          (entry.road || '').toLowerCase().includes(searchLower);
        if (!match) return false;
      }
      
      // Column filters
      if (filters.owner && entry.owner !== filters.owner) return false;
      if (filters.property && entry.property !== filters.property) return false;
      if (filters.phone && entry.phone !== filters.phone) return false;
      if (filters.plot && entry.plot !== filters.plot) return false;
      if (filters.email && entry.email !== filters.email) return false;
      if (filters.road && entry.road !== filters.road) return false;
      
      return true;
    });
  }, [matrikel, search, filters]);
  
  const handleCellSave = async (entryId, field, value) => {
    if (!canEdit) return;
    
    try {
      const entry = matrikel.find(m => m.id === entryId);
      if (!entry) return;
      
      // Don't save if unchanged
      if (entry[field] === value) return;
      
      await ProtectedMatrikelAPI.update(entryId, { [field]: value });
      
      // Update local state
      setMatrikel(prev => prev.map(m => 
        m.id === entryId ? { ...m, [field]: value } : m
      ));
      
      notify('Uppdaterat');
    } catch (err) {
      notify('Kunde inte uppdatera', 'error');
    }
  };
  
  const nextPlotSuggestion = useMemo(() => {
    const existingPlots = matrikel.map(m => parseInt(m.plot) || 0);
    return String(existingPlots.length > 0 ? Math.max(...existingPlots) + 1 : 1);
  }, [matrikel]);

  const handleCreateEntry = async (formData) => {
    if (!canEdit) return;
    setAddSaving(true);
    try {
      const result = await ProtectedMatrikelAPI.create(formData);
      if (result.success && result.matrikel) {
        const createdEntry = result.matrikel;
        setMatrikel(prev => [...prev, createdEntry].sort((a, b) => {
          const aPlot = parseInt(a.plot) || 0;
          const bPlot = parseInt(b.plot) || 0;
          return aPlot - bPlot;
        }));
        setShowAddModal(false);
        notify('Ny rad tillagd');
      }
    } catch (err) {
      notify(err.message || 'Kunde inte lägga till rad', 'error');
    } finally {
      setAddSaving(false);
    }
  };
  
  const handleDelete = async (entry) => {
    if (!isAdmin) return;
    setDeleteConfirm(entry);
  };
  
  const confirmDelete = async () => {
    if (!deleteConfirm || !canEdit) return;
    
    try {
      await ProtectedMatrikelAPI.delete(deleteConfirm.id);
      setMatrikel(prev => prev.filter(m => m.id !== deleteConfirm.id));
      notify('Raderad');
    } catch (err) {
      notify('Kunde inte radera', 'error');
    } finally {
      setDeleteConfirm(null);
    }
  };
  
  const handleExportCSV = async () => {
    if (exportLoading.csv) return;
    setExportLoading(prev => ({ ...prev, csv: true }));
    try {
      const blob = await ProtectedMatrikelAPI.export();
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'fornbo-matrikel.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        notify('CSV exporterad');
      }
    } catch (err) {
      console.error('CSV export error:', err);
      notify('Kunde inte exportera CSV', 'error');
    } finally {
      setExportLoading(prev => ({ ...prev, csv: false }));
    }
  };
  
  const handleExportEmails = async () => {
    if (exportLoading.emails) return;
    setExportLoading(prev => ({ ...prev, emails: true }));
    try {
      const blob = await ProtectedMatrikelAPI.exportEmails();
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'fornbo-email-list.txt';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        notify('E-postlista exporterad');
      }
    } catch (err) {
      console.error('Email export error:', err);
      notify('Kunde inte exportera e-postlista', 'error');
    } finally {
      setExportLoading(prev => ({ ...prev, emails: false }));
    }
  };
  
  const thStyle = {
    padding: '0.85rem 0.75rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--text-soft)',
    borderBottom: '2px solid var(--border)',
    whiteSpace: 'nowrap'
  };
  
  const tdStyle = {
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid var(--border)',
    fontSize: '0.85rem',
    color: 'var(--text)',
    verticalAlign: 'top'
  };
  
  const filterSelectStyle = {
    width: '100%',
    padding: '0.35rem 0.5rem',
    border: '1px solid var(--border)',
    borderRadius: 4,
    fontSize: '0.75rem',
    background: 'var(--white)',
    color: 'var(--text)',
    cursor: 'pointer'
  };
  
  return (
    <div style={{ minHeight: '100vh', background: 'var(--off-white, #f7f6f2)' }}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {deleteConfirm && (
        <DeleteConfirmModal
          entry={deleteConfirm}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
      {showAddModal && (
        <AddEntryModal
          initialPlot={nextPlotSuggestion}
          onSave={handleCreateEntry}
          onCancel={() => setShowAddModal(false)}
          saving={addSaving}
        />
      )}

      {/* ── hero header ── */}
      <div style={{ background: 'var(--forest)', color: '#fff', padding: '3rem 1.5rem 2.5rem' }}>
        <div className="container" style={{ maxWidth: 1200 }}>
          <Link to="/styrelsen/dashboard" style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
            fontSize: '0.78rem', color: 'rgba(255,255,255,.65)', textDecoration: 'none',
            letterSpacing: '0.04em', marginBottom: '1.25rem'
          }}>
            ← Dashboard
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h1 style={{ fontFamily: '"Playfair Display", serif', fontSize: '2.2rem', margin: '0 0 0.5rem', fontWeight: 700 }}>
                Digital Matrikel
              </h1>
              <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,.75)', margin: 0 }}>
                Fastighetsägare och kontaktuppgifter för Fornbo samfällighet
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                onClick={handleExportCSV}
                disabled={exportLoading.csv}
                className="btn btn-ghost"
                style={{ 
                  fontSize: '0.78rem',
                  background: exportLoading.csv ? 'rgba(255,255,255,.05)' : 'rgba(255,255,255,.1)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,.3)',
                  opacity: exportLoading.csv ? 0.6 : 1,
                  cursor: exportLoading.csv ? 'not-allowed' : 'pointer'
                }}
              >
                {exportLoading.csv ? '⏳ Exporterar...' : '📥 Exportera CSV'}
              </button>
              <button 
                onClick={handleExportEmails}
                disabled={exportLoading.emails}
                className="btn btn-ghost"
                style={{ 
                  fontSize: '0.78rem',
                  background: exportLoading.emails ? 'rgba(255,255,255,.05)' : 'rgba(255,255,255,.1)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,.3)',
                  opacity: exportLoading.emails ? 0.6 : 1,
                  cursor: exportLoading.emails ? 'not-allowed' : 'pointer'
                }}
              >
                {exportLoading.emails ? '⏳ Exporterar...' : '📧 E-postlista'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── content ── */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem 1.5rem' }}>
        
        {/* Toolbar */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '1rem', 
          marginBottom: '1.5rem',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', gap: '0.75rem', flex: 1, minWidth: 280 }}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök i alla kolumner..."
              style={{
                flex: 1,
                padding: '0.6rem 1rem',
                border: '1px solid var(--border)',
                borderRadius: 4,
                fontSize: '0.9rem',
                background: 'var(--white)'
              }}
            />
            {search && (
              <button 
                onClick={() => setSearch('')}
                style={{
                  padding: '0.6rem 1rem',
                  background: 'var(--cream)',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
              >
                Rensa
              </button>
            )}
          </div>
          
          {canEdit && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-forest"
              style={{ fontSize: '0.85rem' }}
            >
              + Lägg till rad
            </button>
          )}
        </div>
        
        {/* Results count */}
        <div style={{ fontSize: '0.78rem', color: 'var(--text-soft)', marginBottom: '1rem' }}>
          Visar {filtered.length} av {matrikel.length} poster
        </div>
        
        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
            <div>Laddar matrikel...</div>
          </div>
        ) : (
          <div style={{
            background: 'var(--white)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
              <thead>
                <tr style={{ background: 'var(--cream)' }}>
                  <th style={thStyle}>Tomt</th>
                  <th style={thStyle}>Fastighet</th>
                  <th style={thStyle}>Ägare/delägare</th>
                  <th style={thStyle}>Telefon</th>
                  <th style={thStyle}>E-post</th>
                  <th style={thStyle}>Väg</th>
                  <th style={{ ...thStyle, width: '1%', textAlign: 'center', whiteSpace: 'nowrap' }}>Åtgärder</th>
                </tr>
                <tr style={{ background: 'var(--cream)' }}>
                  <th style={{ ...thStyle, padding: '0.5rem' }}>
                    <select 
                      value={filters.plot} 
                      onChange={(e) => setFilters({...filters, plot: e.target.value})}
                      style={filterSelectStyle}
                    >
                      <option value="">Alla</option>
                      {filterOptions.plot.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </th>
                  <th style={{ ...thStyle, padding: '0.5rem' }}>
                    <select 
                      value={filters.property} 
                      onChange={(e) => setFilters({...filters, property: e.target.value})}
                      style={filterSelectStyle}
                    >
                      <option value="">Alla</option>
                      {filterOptions.property.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </th>
                  <th style={{ ...thStyle, padding: '0.5rem' }}>
                    <select 
                      value={filters.owner} 
                      onChange={(e) => setFilters({...filters, owner: e.target.value})}
                      style={filterSelectStyle}
                    >
                      <option value="">Alla</option>
                      {filterOptions.owner.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </th>
                  <th style={{ ...thStyle, padding: '0.5rem' }}>
                    <select 
                      value={filters.phone} 
                      onChange={(e) => setFilters({...filters, phone: e.target.value})}
                      style={filterSelectStyle}
                    >
                      <option value="">Alla</option>
                      {filterOptions.phone.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </th>
                  <th style={{ ...thStyle, padding: '0.5rem' }}>
                    <select 
                      value={filters.email} 
                      onChange={(e) => setFilters({...filters, email: e.target.value})}
                      style={filterSelectStyle}
                    >
                      <option value="">Alla</option>
                      {filterOptions.email.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </th>
                  <th style={{ ...thStyle, padding: '0.5rem' }}>
                    <select 
                      value={filters.road} 
                      onChange={(e) => setFilters({...filters, road: e.target.value})}
                      style={filterSelectStyle}
                    >
                      <option value="">Alla</option>
                      {filterOptions.road.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </th>
                  {canEdit && <th style={thStyle}></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((entry) => (
                  <tr key={entry.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={tdStyle}>
                      <EditableCell
                        value={entry.plot}
                        onSave={(value) => handleCellSave(entry.id, 'plot', value)}
                        isEditing={editingCell === `${entry.id}-plot`}
                        setIsEditing={(editing) => setEditingCell(editing ? `${entry.id}-plot` : null)}
                        disabled={!canEdit}
                        onTabNext={() => setEditingCell(`${entry.id}-property`)}
                      />
                    </td>
                    <td style={tdStyle}>
                      <EditableCell
                        value={entry.property}
                        onSave={(value) => handleCellSave(entry.id, 'property', value)}
                        isEditing={editingCell === `${entry.id}-property`}
                        setIsEditing={(editing) => setEditingCell(editing ? `${entry.id}-property` : null)}
                        disabled={!canEdit}
                        onTabPrev={() => setEditingCell(`${entry.id}-plot`)}
                        onTabNext={() => setEditingCell(`${entry.id}-owner`)}
                      />
                    </td>
                    <td style={tdStyle}>
                      <EditableCell
                        value={entry.owner}
                        onSave={(value) => handleCellSave(entry.id, 'owner', value)}
                        isEditing={editingCell === `${entry.id}-owner`}
                        setIsEditing={(editing) => setEditingCell(editing ? `${entry.id}-owner` : null)}
                        disabled={!canEdit}
                        isTextArea
                        onTabPrev={() => setEditingCell(`${entry.id}-property`)}
                        onTabNext={() => setEditingCell(`${entry.id}-phone`)}
                      />
                    </td>
                    <td style={tdStyle}>
                      <EditableCell
                        value={entry.phone}
                        onSave={(value) => handleCellSave(entry.id, 'phone', value)}
                        isEditing={editingCell === `${entry.id}-phone`}
                        setIsEditing={(editing) => setEditingCell(editing ? `${entry.id}-phone` : null)}
                        disabled={!canEdit}
                        isTextArea
                        onTabPrev={() => setEditingCell(`${entry.id}-owner`)}
                        onTabNext={() => setEditingCell(`${entry.id}-email`)}
                      />
                    </td>
                    <td style={tdStyle}>
                      <EditableCell
                        value={entry.email}
                        onSave={(value) => handleCellSave(entry.id, 'email', value)}
                        isEditing={editingCell === `${entry.id}-email`}
                        setIsEditing={(editing) => setEditingCell(editing ? `${entry.id}-email` : null)}
                        disabled={!canEdit}
                        isTextArea
                        onTabPrev={() => setEditingCell(`${entry.id}-phone`)}
                        onTabNext={() => setEditingCell(`${entry.id}-road`)}
                      />
                    </td>
                    <td style={tdStyle}>
                      <EditableCell
                        value={entry.road}
                        onSave={(value) => handleCellSave(entry.id, 'road', value)}
                        isEditing={editingCell === `${entry.id}-road`}
                        setIsEditing={(editing) => setEditingCell(editing ? `${entry.id}-road` : null)}
                        disabled={!canEdit}
                        isTextArea
                        onTabPrev={() => setEditingCell(`${entry.id}-email`)}
                      />
                    </td>
                    <td style={{ ...tdStyle, width: '1%', whiteSpace: 'nowrap', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                        <button
                          onClick={() => {
                            const address = `Fornbo ${entry.road || ''} ${entry.plot || ''}`.trim();
                            const encodedAddress = encodeURIComponent(address);
                            window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
                          }}
                          title={`Visa på karta: Fornbo ${entry.road || ''} ${entry.plot || ''}`.trim()}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.35rem',
                            color: 'var(--text-soft)',
                            fontSize: '1rem'
                          }}
                          onMouseEnter={e => e.target.style.color = 'var(--forest)'}
                          onMouseLeave={e => e.target.style.color = 'var(--text-soft)'}
                        >
                          🗺️
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(entry)}
                            title="Ta bort"
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0.35rem',
                              color: 'var(--text-soft)',
                              fontSize: '1rem'
                            }}
                            onMouseEnter={e => e.target.style.color = 'var(--danger)'}
                            onMouseLeave={e => e.target.style.color = 'var(--text-soft)'}
                          >
                            🗑
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-soft)' }}>
                Inga poster hittades
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
